import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import type { BoxProps } from '@mui/material';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { stegaClean } from '@sanity/client/stega';
import type { FC } from 'react';
import { Button } from '~/components/atoms/Button';

import { ImageSanity } from '~/components/atoms/ImageSanity';
import type { ImageTextProps } from '~/types/sanity/components';
import { checkAuthVisibility } from '~/utils/auth';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { buildSanityLinkUrl } from '~/utils/link';

const verticalAlignMap: Record<ImageTextProps['verticalAlignContent'], BoxProps['alignSelf']> = {
  Top: 'flex-start',
  Middle: 'center',
  Bottom: 'flex-end',
};

export const ImageText: FC<Omit<ImageTextProps, '_key' | '_type'>> = ({
  heading,
  imageDesktopObject,
  imageMobileObject,
  isAuthenticated,
  links,
  text: contentText,
  verticalAlignContent,
  verticalAlignImage,
  widthContent,
  widthImage,
}) => {
  const theme = useTheme();
  const getCompoundTestId = getCompoundTestIdGenerator('imageText');
  const verticalAlignContentValue = verticalAlignMap[verticalAlignContent];
  const verticalAlignImageValue = verticalAlignMap[verticalAlignImage];

  const layoutType = imageDesktopObject || imageMobileObject ? 'imageText' : 'text';
  const cleanedWidthContent = stegaClean(widthContent);
  const cleanedwidthImage = stegaClean(widthImage);

  /**
   * (Desktop Only)
   * Calculate width of content and image columns based on the column gap.
   * If the content or image column is 100%, there is no gap deduction from either width.
   * If both content and image columns are not 100%, the gap number is divided by 2 and deducted from each width.
   *
   * This is to ensure that the total width of the content and image columns is equal to the parent container.
   * Doing this ensures that the Sanity controls for the width of the content and image columns are accurate. And that
   * if the content or image column is set to 100%, the other column won't have a gap deduction from it's width.
   */
  const columnGapNumber =
    cleanedWidthContent !== '100%' && cleanedwidthImage !== '100%'
      ? // 8 is the spacing unit
        6 * 8
      : 0;
  const widthContentColumn =
    cleanedWidthContent === '100%'
      ? '100%'
      : columnGapNumber
        ? `calc(${cleanedWidthContent} - ${columnGapNumber / 2}px)`
        : cleanedWidthContent;
  const widthImageColumn =
    cleanedwidthImage === '100%'
      ? '100%'
      : columnGapNumber
        ? `calc(${cleanedwidthImage} - ${columnGapNumber / 2}px)`
        : cleanedwidthImage;

  const visibleLinks = links ? links.filter((link) => checkAuthVisibility(link.authVisibility, isAuthenticated)) : [];

  return (
    <Box sx={{ py: { xs: 5, sm: 7.5 } }}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 2, sm: 6 },
          }}
        >
          {/* Content */}
          <Box
            data-testid={getCompoundTestId('contentColumn')}
            sx={{
              width: { xs: '100%', sm: widthContentColumn },
              alignSelf: verticalAlignContentValue,
              textAlign: { xs: 'left', sm: layoutType === 'imageText' ? 'right' : 'left' },
              ...(layoutType === 'text' && {
                display: { sm: 'flex' },
                gap: { sm: 6 },
                alignItems: { sm: 'center' },
              }),
            }}
          >
            {/* Heading & Image Mobile */}
            <Box
              sx={{
                ...(!!imageMobileObject && {
                  display: { xs: 'flex', sm: 'block' },
                  gap: 1.875,
                }),
                ...(layoutType === 'text' && { width: '100%' }),
              }}
            >
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontSize: { md: theme.typography.h3.fontSize },
                  lineHeight: { md: theme.typography.h3.lineHeight },
                  ...(!!imageMobileObject && { alignSelf: 'flex-end' }),
                }}
              >
                {heading}
              </Typography>

              {/* Image (Mobile) */}
              {imageMobileObject && (
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: 136, flexShrink: 0 }}>
                  <ImageSanity
                    height={338}
                    imageObject={imageMobileObject}
                    testId={getCompoundTestId('mobileImage')}
                    width={272}
                  />
                </Box>
              )}
            </Box>

            {/* Content Text & Links */}
            <Box>
              {contentText && (
                <Typography
                  variant="body1"
                  component="p"
                  sx={{
                    fontSize: { md: 20 },
                    mt: { xs: 3, sm: layoutType === 'text' ? 0 : 3 },
                  }}
                >
                  {contentText}
                </Typography>
              )}

              {!!visibleLinks.length && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: 4.5, sm: 2.25 },
                    justifyContent: { xs: 'flex-start', sm: layoutType === 'imageText' ? 'flex-end' : 'flex-start' },
                    mt: 4.5,
                  }}
                >
                  {visibleLinks.map((link, index) => (
                    <Button
                      data-testid={getCompoundTestId('button', index.toString())}
                      endIcon={<ChevronRightOutlined />}
                      id={link.id}
                      key={index}
                      renderAsLink
                      sx={{ whiteSpace: 'nowrap' }}
                      to={buildSanityLinkUrl(link)}
                      variant="text"
                    >
                      {link.label}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Image (Desktop) */}
          {imageDesktopObject && (
            <Box
              data-testid={getCompoundTestId('imageColumn')}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '100%', sm: widthImageColumn },
                alignSelf: verticalAlignImageValue,
                flexShrink: 0,
              }}
            >
              <ImageSanity
                height={1070}
                imageObject={imageDesktopObject}
                testId={getCompoundTestId('desktopImage')}
                width={1180}
              />
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};
